<template>
  <div class="aggregate-search">
    <div class="container">
      <div class="aggregate-search__content">
        <div class="row">
          <div :class="hideFilters ? 'col-lg-12' : 'col-lg-9'">
            <div v-if="loading">
              <div class="aggregate-search__loading">
                <font-awesome-icon icon="spinner"
                                   spin></font-awesome-icon>
              </div>
            </div>

            <div v-else
                 class="aggregate-search__results">
              <div v-if="!searchTerm">
                Enter a search term to get started.
              </div>

              <div v-else-if="results.length === 0"
                   class="aggregate-search__no-results">
                No results
              </div>

              <div v-for="result in results"
                   v-else
                   :key="result.url"
                   class="aggregate-search__result">
                <AggregateSearchResult v-bind="result"></AggregateSearchResult>
              </div>
            </div>

            <div v-if="searchTerm && results.length > 0"
                 class="aggregate-search__pagination">
              <ProductSearchPagination :page-count="pageCount"
                                       :page-index="pageIndex"
                                       @changePage="changePage"></ProductSearchPagination>
            </div>
          </div>

          <div
            v-if="!hideFilters"
            class="col-lg-3">
            <div class="aggregate-search__filters">
              <h2 class="aggregate-search__filters-heading sr-only">
                Filter results:
              </h2>

              <TagCheckboxes
                v-model="selectedThemes"
                group="default"
                filter="theme"
                :options="themeOptions"
                :config="{}"
                name="Theme"
                hide-counts
                no-vuex></TagCheckboxes>

              <TagCheckboxes
                v-model="selectedProductTypes"
                group="default"
                filter="productType"
                :options="productTypeOptions"
                :config="{}"
                name="Product type"
                hide-counts
                no-vuex></TagCheckboxes>

              <TagCheckboxes
                v-model="selectedContentTypes"
                group="default"
                filter="contentType"
                :options="contentTypeOptions"
                :config="{}"
                name="Content type"
                hide-counts
                no-vuex></TagCheckboxes>

              <TagCheckboxes
                v-model="selectedCustomerTypes"
                group="default"
                filter="customerType"
                :options="customerTypeOptions"
                :config="{}"
                name="Customer type"
                hide-counts
                no-vuex></TagCheckboxes>

              <TagCheckboxes
                v-model="selectedTopics"
                group="default"
                filter="topic"
                :options="topicOptions"
                :config="{}"
                name="Topic"
                hide-counts
                no-vuex></TagCheckboxes>

              <TagCheckboxes
                v-model="selectedSubThemes"
                group="default"
                filter="subTheme"
                :options="subThemeOptions"
                :config="{}"
                name="Team"
                hide-counts
                no-vuex></TagCheckboxes>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { TweenMax } from "gsap";
import axios from "axios";
import Uri from "urijs";
import AggregateSearchResult from "~/Components/Search/AggregateSearchResult.vue";
import ProductSearchPagination from "~/Components/Search/ProductSearchPagination.vue";
import TagCheckboxes from "~/Components/Search/Filters/TagCheckboxes.vue";
import { getCategorySortIndex } from "~/Scripts/modules/search";

const uri = new Uri( window.document.location.href );
const queryString = uri.search( true );

const parameters = {
  pageIndex: 0,
  selectedThemes: [],
  selectedProductTypes: [],
  selectedContentTypes: [],
  selectedCustomerTypes: [],
  selectedTopics: [],
  selectedSubThemes: []
};

if ( queryString ) {
  if ( queryString.page ) {
    parameters.pageIndex = queryString.page - 1;
  }

  if ( queryString.theme ) {
    parameters.selectedThemes = queryString.theme.split( "," );
  }

  if ( queryString.productType ) {
    parameters.selectedProductTypes = queryString.productType.split( "," );
  }

  if ( queryString.contentType ) {
    parameters.selectedContentTypes = queryString.contentType.split( "," );
  }

  if ( queryString.customerType ) {
    parameters.selectedCustomerTypes = queryString.customerType.split( "," );
  }

  if ( queryString.topic ) {
    parameters.selectedTopics = queryString.topic.split( "," );
  }

  if ( queryString.team ) {
    parameters.selectedSubThemes = queryString.team.split( "," );
  }
}

export default {
  name: "AggregateSearch",
  components: {
    AggregateSearchResult,
    ProductSearchPagination,
    TagCheckboxes
  },
  props: {
    nodeId: {
      type: Number,
      required: true
    },
    themeAttributes: {
      type: Array,
      required: true
    },
    productTypeAttributes: {
      type: Array,
      required: true
    },
    customerTypeAttributes: {
      type: Array,
      required: true
    },
    contentTypeAttributes: {
      type: Array,
      required: true
    },
    topicAttributes: {
      type: Array,
      required: true
    },
    subThemeAttributes: {
      type: Array,
      required: true
    },
    hideFilters: {
      type: Boolean,
      default: false
    },
    searchTerm: {
      type: String,
      default: ""
    }
  },
  data: () => ( {
    results: [],
    resultsPerPage: 24,
    resultCount: 0,
    loading: true,
    selectedThemes: [],
    selectedProductTypes: [],
    selectedContentTypes: [],
    selectedCustomerTypes: [],
    selectedTopics: [],
    selectedSubThemes: [],
    pageIndex: 0,
    ...parameters
  } ),
  computed: {
    pageCount () {
      let result = 0;

      if ( this.resultCount > 0 ) {
        result = Math.ceil( this.resultCount / this.resultsPerPage );
      }

      return result;
    },
    themeOptions () {
      let result = [];

      if ( this.themeAttributes ) {
        result = this.themeAttributes.map( x => ( {
          key: x.id,
          value: `${x.id}=${x.name}`,
          count: 1
        } ) );
      }

      return result;
    },
    productTypeOptions () {
      let result = [];

      if ( this.productTypeAttributes ) {
        result = this.productTypeAttributes.map( x => ( {
          key: x.id,
          value: `${x.id}=${x.name}`,
          count: 1
        } ) );

        result.sort( ( a, b ) => {
          const ai = getCategorySortIndex( a.label );
          const bi = getCategorySortIndex( b.label );
          return ai - bi;
        } );
      }

      return result;
    },
    contentTypeOptions () {
      let result = [];

      if ( this.contentTypeAttributes ) {
        result = this.contentTypeAttributes.map( x => ( {
          key: x.id,
          value: `${x.id}=${x.name}`,
          count: 1
        } ) );
      }

      return result;
    },
    customerTypeOptions () {
      let result = [];

      if ( this.customerTypeAttributes ) {
        result = this.customerTypeAttributes.map( x => ( {
          key: x.id,
          value: `${x.id}=${x.name}`,
          count: 1
        } ) );
      }

      return result;
    },
    topicOptions () {
      let result = [];

      if ( this.topicAttributes ) {
        result = this.topicAttributes.map( x => ( {
          key: x.id,
          value: `${x.id}=${x.name}`,
          count: 1
        } ) );
      }

      return result;
    },
    subThemeOptions () {
      let result = [];

      if ( this.subThemeAttributes ) {
        result = this.subThemeAttributes.map( x => ( {
          key: x.id,
          value: `${x.id}=${x.name}`,
          count: 1
        } ) );
      }

      return result;
    }
  },
  watch: {
    pageIndex ( val ) {
      this.updateQueryString( "page", val + 1 );
      this.fetchData();
    },
    selectedThemes: {
      deep: true,
      handler ( val ) {
        this.pageIndex = 0;
        this.updateQueryString( "theme", val?.join( "," ) );
        this.fetchData();
      }
    },
    selectedProductTypes: {
      deep: true,
      handler ( val ) {
        this.pageIndex = 0;
        this.updateQueryString( "productType", val?.join( "," ) );
        this.fetchData();
      }
    },
    selectedCustomerTypes: {
      deep: true,
      handler ( val ) {
        this.pageIndex = 0;
        this.updateQueryString( "customerType", val?.join( "," ) );
        this.fetchData();
      }
    },
    selectedContentTypes: {
      deep: true,
      handler ( val ) {
        this.pageIndex = 0;
        this.updateQueryString( "contentType", val?.join( "," ) );
        this.fetchData();
      }
    },
    selectedTopics: {
      deep: true,
      handler ( val ) {
        this.pageIndex = 0;
        this.updateQueryString( "topic", val?.join( "," ) );
        this.fetchData();
      }
    },
    selectedSubThemes: {
      deep: true,
      handler ( val ) {
        this.pageIndex = 0;
        this.updateQueryString( "team", val?.join( "," ) );
        this.fetchData();
      }
    }
  },
  created () {
    this.fetchData();
  },
  methods: {
    fetchData () {
      this.loading = true;

      if ( this.searchTerm ) {
        axios.post( "/umbraco/Surface/AggregateSearch/Search", {
          nodeId: this.nodeId,
          pageIndex: this.pageIndex,
          themeAttributes: this.selectedThemes,
          productTypeAttributes: this.selectedProductTypes,
          customerTypeAttributes: this.selectedCustomerTypes,
          contentTypeAttributes: this.selectedContentTypes,
          topicAttributes: this.selectedTopics,
          subThemeAttributes: this.selectedSubThemes,
          searchTerm: this.searchTerm
        } )
          .then( ( { data } ) => {
            this.results = data.results;
            this.resultCount = data.resultCount;
            this.resultsPerPage = data.resultsPerPage;

            this.$emit( "update", data.resultCount );
          } )
          .finally( () => {
            this.loading = false;
          } );
      } else {
        this.results = [];
        this.resultsPerPage = 24;
        this.resultCount = 0;

        this.$emit( "update", 0 );
        this.loading = false;
      }
    },
    updateQueryString ( key, value ) {
      let newPageUrl = window.location.href;
      const param = {};
      param[key] = value;

      if ( value ) {
        newPageUrl = Uri( newPageUrl ).setQuery( param );
      } else {
        newPageUrl = Uri( newPageUrl ).removeQuery( key );
      }

      window.history.pushState( {}, "", newPageUrl );
    },
    changePage ( val ) {
      this.pageIndex = val;
    },
    beforeEnter ( el ) {
      el.style.height = "auto";
      el.style.overflow = "hidden";
    },
    enter ( el, done ) {
      TweenMax.from(
        el,
        0.2,
        {
          height: "0",
          onComplete: () => {
            el.style.overflow = "auto";
            done();
          }
        }
      );
    },
    beforeLeave ( el ) {
      el.style.overflow = "hidden";
    },
    leave ( el, done ) {
      TweenMax.to(
        el,
        0.2,
        {
          height: 0,
          onComplete: () => {
            el.style.overflow = "auto";
            done();
          }
        }
      );
    }
  }
};
</script>

<style lang="less" scoped>
@import "../../Styles/utils/colors.less";

.aggregate-search {
  margin: 30px 0;

  &__content {
    padding: 0 10px 15px;

    @media ( max-width: 991px ) {
      padding: 15px 15px 0;
    }
  }

  &__filters {
    margin-bottom: 15px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: stretch;

    @media ( max-width: 991px ) {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  &__filters-heading {
    color: @color-accent-6;
    font-weight: 600;
    font-size: 14px;
    margin-bottom: 15px;
    flex-shrink: 0;

    @media ( max-width: 991px ) {
      margin-bottom: 15px;
    }
  }

  &__results {
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
  }

  &__result {
    flex-basis: calc( 33.33% - 15px );
    flex-grow: 0;
    flex-shrink: 0;
    display: flex;

    @media ( max-width: 991px ) {
      flex-basis: 100%;
    }
  }

  &__no-results {
    text-align: center;
    font-weight: 600;
    color: #006a7f;
    flex-grow: 1;
    padding-top: 30px;
    padding-bottom: 30px;
  }

  &__loading {
    text-align: center;
    font-size: 50px;
    color: @color-accent-6;
  }

  &__textbox {
    border: 2px solid #bbdbdd;
    padding: 2px 5px;
    font-size: 14px;
    width: 100%;

    @media ( min-width: 992px ) {
      width: 220px;
    }
  }
}

.search-filters {
  width: 100%;

  @media ( max-width: 991px ) {
    margin-bottom: 20px;
  }
}
</style>
